import React from 'react';
import { Typography, Paper, Grid } from '@mui/material';

// Section 0: For Instructors: June 2025 LLM Performance on Homework
const Instructors2025Section = () => {
  const homeworkSubsectionBackgroundColor = '#fff7fe';

  return (
    <>
      <Typography variant="h4" gutterBottom>
        For Instructors: June 2025 LLM Performance on Homework
      </Typography>
      <Paper style={{ padding: '20px', backgroundColor: '#ffe8fd' }}>
        <Typography variant="body1" paragraph align="left">
          <p>
          LLMs are now much better at undergraduate and graduate homework than they were a year ago.
          If your reaction is "they aren't that great I tried it 11 months ago" you should try again
          on the paid models. The free models are worse. Try o4-mini-high and Claude Sonnet 4, once again
          not the free models.
            </p>
          <p>
            We now have a slack called AI & Pedagogy and Undergrad CS Theory where faculty interested in
how to teach undergraduate computer science (with a focus on theory classes) given the existence of LLMs.
If you would like to join please email at <a href="mailto:andrea2@bu.edu">andrea2@bu.edu</a>. We would be
            excited to have anyone teaching undergraduate theory classes join us!
          </p>

          Here are some observations:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Paper style={{ padding: '10px', backgroundColor: homeworkSubsectionBackgroundColor }}>
              <Typography variant="h5">Undergraduate Homework</Typography>
              <Typography align="left" paragraph>

                The paid tier of models solve basically all undergradute CS problems we have given them. We have found one question
                in a complexity theory class that is given to undergraduates which o4-mini-high and claude 3.7 sonnet
                didn't solve. I really want to emphasize this: if you are teaching introductory computer science content
                you should expect LLMs to get 100% correctness on all of the questions you assign students for homework.
                If you say this isn't true but haven't actually run your homework through paid models people will not
                believe you.

                Free tier LLMs do very well on undergraduate homework, but gets things wrong sometimes. Something around 80%
                on introductory algorithms content, introductory linear algebra for computer science, etc.
              </Typography>
              <Typography align="left">
                How we should change what and how we are teaching has become more urgent.
                 I am still very interested in ideas about how to change how we
                teach, but also WHAT we teach in introductory algorithms in response to these models. For
                the question of how, a greater focus on in-class evaluations is a popular answer.

                <p>
                However,
                when it comes to the question of changing the contents of the courses, despite having had
                many discussions with faculty from many institutions, none of us seem to have satisfactory
                answers. Let me highlight two knee-jerk reaction answers I have received both of which are, in my oppinion,
                  insufficient.
                  </p>
                  <p>
                    First, some people suggest that we shouldn't change anything at all. We should put the same information
                    into students heads, and just change up evaluation to have more in-class exams, quizzes etc.
                    I just don't think this is taking seriously how big a deal LLMs that can solve almost all of CS
                    undergrad are.
                    </p>
                    <p>
                      Second is a reaction that we should stop teaching anything any tool can solve. I want to be clear
                      this comes from a reasonable place: we should seek to not waste people's time. However,
                      the issues with this are most obvious when you think about a students first coding class.
                      Any question appropriate to ask a student learning to code for the first time an LLM can also solve.
                      But, there is still need for developers who can outperform these models. I don't know how to
                      get someone to the point of outperforming without making them write their first for loop.
                    </p>
                <p>
                  The synthesis I don't know how to form yet is something like addition in the age of calculators.
                  I am glad I know addition, I am also glad that schools spend less time drilling addition tables than
                  they did before calculators. I am striving to find a similar tradeoff space, but it is hard and I
                  am not sure how.
                </p>
                    <p>
                There are, presumably, concepts and techniques we can now skip or reduce our focus on, but
                it isn't clear which concepts those are. If you have compelling answers about what should
                change about the content of these classes I am very interested in hearing from you at{' '}
                <a href="mailto:andrea2@bu.edu">andrea2@bu.edu</a>.
                  </p>
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper style={{ padding: '10px', backgroundColor: homeworkSubsectionBackgroundColor }}>
              <Typography variant="h5">Graduate Homework</Typography>
              <Typography align="left">
                <p>
                Graduate-level assignments often involve more complex and abstract problems. Modern LLMs
                (Claude 3.7 sonnet, o4-mini-high) all got 66% of my grad homework! Single shot! No special prompting!
                I tried this on some other people's grad homework and got the similar result of around 50%.
                I haven't tried Claude Sonnet 4 on my homework yet.
                  </p>

                My class is proof based and when the model doesn't solve the problem it will give a buggy proof and
                declare QED. I will allow (and encorage!) LLM usage in my grad class because I want students to get
                used to using models to help them accelerate their research progress.
              </Typography>
            </Paper>

            <Paper
              style={{
                padding: '10px',
                backgroundColor: homeworkSubsectionBackgroundColor,
                marginTop: '20px',
              }}
            >
              <Typography variant="h5">My Summary and Takeaways</Typography>
              <p></p>
              <Typography align="left" paragraph>
                My general takeaway from my own experience and from speaking to other faculty who have tried
                to measure the performance of LLMs in their classes is this: models as of June 2025 crush undergraduate
                computer science content and are surprisingly good at grad level homework.
                I even more strongly than before suggest faculty take the time (I promise it doesn't take long) to run
                at least a few assignments through the models to understand what the models are capable of.
              </Typography>
              <Typography align="left">
                I am very curious to see what improvements come from the next generation of models. They are already
                quite decent at graduate homework. I and many other faculty are working on trying to use LLMs to
                prove theorems.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

// Section 1: For Instructors: June 2024 LLM Performance on Homework
const Instructors2024Section = () => {
  const homeworkSubsectionBackgroundColor = '#ebf4fc';

  return (
    <>
      <Typography variant="h4" gutterBottom>
        For Instructors: June 2024 LLM Performance on Homework
      </Typography>
      <Paper style={{ padding: '20px', backgroundColor: '#dbeaf5' }}>
        <Typography variant="body1" paragraph align="left">
          LLMs have shown varying degrees of success when applied to undergraduate and graduate-level
          homework. Here are some observations:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Paper style={{ padding: '10px', backgroundColor: homeworkSubsectionBackgroundColor }}>
              <Typography variant="h5">Undergraduate Homework</Typography>
              <Typography align="left" paragraph>
                LLMs perform passably on undergraduate assignments, often providing correct solutions and
                useful explanations. If you haven't run the homework you are giving through an LLM to see
                the kinds of answers it gives I strongly encourage you to do so. In my experience modern
                LLMs (GPT-4, GPT-4o, &amp; Claude Opus) get about 75% of homework questions exactly correct.
                On the remaining quarter the mistakes vary from the bizarre (and profoundly wrong) to the
                minor and very human-seeming. Once again, I strongly encourage you to try running your
                homework through so you can understand the current abilities of LLMs on the problems you
                assign.
              </Typography>
              <Typography align="left">
                It is worth noting that if students rely on these models to do their homework the student's
                grades on exams will likely suffer. I am very interested in ideas about how to change how we
                teach, but also WHAT we teach in introductory algorithms in response to these models. For
                the question of how, a greater focus on in-class evaluations is a popular answer. However,
                when it comes to the question of changing the contents of the courses, despite having had
                many discussions with faculty from many institutions, none of us seem to have satisfactory
                answers. Notably, most of the fundamental concepts still need to be understood by students.
                There are, presumably, concepts and techniques we can now skip or reduce our focus on, but
                it isn't clear which concepts those are. If you have compelling answers about what should
                change about the content of these classes I am very interested in hearing from you at{' '}
                <a href="mailto:andrea2@bu.edu">andrea2@bu.edu</a>.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper style={{ padding: '10px', backgroundColor: homeworkSubsectionBackgroundColor }}>
              <Typography variant="h5">Graduate Homework</Typography>
              <Typography align="left">
                Graduate-level assignments often involve more complex and abstract problems. Modern LLMs
                (GPT-4, GPT-4o, &amp; Claude Opus) all got 0% of my grad homework (with my prompts single
                shot). On the easiest problem of the semester I was able to coax the models to get the right
                answer. On the harder problems even with hints and hand-holding the models couldn't solve
                the problems. In fact, I gave out extra credit to any student who could find a method to get
                an LLM to produce correct solutions to homework problems. I got some attempts from students
                but none of their approaches worked.
              </Typography>
            </Paper>

            <Paper
              style={{
                padding: '10px',
                backgroundColor: homeworkSubsectionBackgroundColor,
                marginTop: '20px',
              }}
            >
              <Typography variant="h5">My Summary and Takeaways</Typography>
              <Typography align="left" paragraph>
                My general takeaway from my own experience and from speaking to other faculty who have tried
                to measure the performance of LLMs in their classes is this: models of June 2024 perform
                passably at the undergraduate level material and perform terribly at graduate level
                material. I strongly suggest faculty take the time (I promise it doesn't take long) to run
                at least a few assignments through the models to understand what the models are capable of.
              </Typography>
              <Typography align="left">
                I am very curious to see what improvements come from the next generation of models. Notably,
                if the models are good at grad-level homework it suggests possible application to research
                progress directly!
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

// Section 2: (From 2023) Teaching People to Code with LLMs
const TeachingLLMCoding2023Section = () => {
  const codingSubsectionBackgroundColor = '#f1edf8';

  return (
    <>
      <Typography variant="h4" gutterBottom>
        (From 2023) Teaching People to Code with LLMs
      </Typography>
      <Paper
        style={{ padding: '20px', backgroundColor: '#F3E5F5', marginBottom: '45px' }}
      >
        <Typography variant="body1" paragraph align="left">
          In 2023 I thought learning to code can be challenging, but Large Language Models (LLMs)
          like GPT-4 can make it easier. Vibe coding is now ubiquitous and there are many tutorials
          and instructions. I have been exploring this in small groups of undergraduates who have a
          mix of different backgrounds in coding and CS. Generally, this new tool empowers students
          to build websites and write code that would otherwise have been above their level. Here
          are some ways LLMs can assist in coding education:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Paper style={{ padding: '10px', backgroundColor: codingSubsectionBackgroundColor }}>
              <Typography variant="h5">1. Code Generation</Typography>
              <Typography align="left">
                LLMs can generate code snippets based on descriptions, saving time and reducing
                errors. Notably, students with background in one language can code in a new language
                with extremely short (think 1 hour) turnaround time for basic tasks.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper style={{ padding: '10px', backgroundColor: codingSubsectionBackgroundColor }}>
              <Typography variant="h5">2. Code Explanation</Typography>
              <Typography align="left">
                LLMs can explain code in plain language, helping beginners understand new concepts.
                This (for now) has important limitations without some kind of human assistance for
                students with no background. Code can feel so impenetrable that students don't trust
                the LLM's advice.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper style={{ padding: '10px', backgroundColor: codingSubsectionBackgroundColor }}>
              <Typography variant="h5">3. Debugging Assistance</Typography>
              <Typography align="left">
                LLMs can assist in debugging by suggesting potential fixes and explaining errors.
                This is especially useful when someone with e.g. a background in Python but not
                JavaScript is programming in JS for the first time.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper style={{ padding: '10px', backgroundColor: codingSubsectionBackgroundColor }}>
              <Typography variant="h5">4. Learning Resources/Teaching Assistance</Typography>
              <Typography align="left">
                LLMs can provide personalized learning resources and answer questions on demand. This
                can serve as a useful "TA" for students who want to ask basic questions and get answers
                almost instantaneously.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

// Main Page: LLMCodingPage
const LLMCodingPage = () => {
  return (
    <div style={{ padding: '20px' }}>
      <Instructors2025Section />
      <p> </p>
      <Instructors2024Section />
      <p> </p>
      <TeachingLLMCoding2023Section />
    </div>
  );
};

export default LLMCodingPage;
